/**
 * Reading the server's live-update stream.
 *
 * Deliberately not `EventSource`. That API cannot set request headers, so
 * authenticating it means putting the access token in the query string — where
 * it lands in server access logs and browser history. Reading the stream with
 * `fetch` keeps the token in an `Authorization` header at the cost of writing
 * the reconnect logic that `EventSource` would have given us. That trade is
 * worth making once, here.
 */

import { API_URL } from "./trpc.js";
import { ensureFreshSession, getAccessToken } from "./session.js";

export interface LiveEvent {
  type: string;
  [key: string]: unknown;
}

/** Backoff between reconnects, in ms. Caps out rather than growing forever. */
const RETRY_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

export interface LiveConnection {
  close: () => void;
}

/**
 * Subscribe to a group's events until `close()` is called.
 *
 * `onEvent` may be called at any time, including long after the caller stopped
 * caring, so callers should treat it as advisory and cheap.
 */
export function connectToGroupEvents(
  groupId: string,
  onEvent: (event: LiveEvent) => void,
  onStateChange?: (connected: boolean) => void,
): LiveConnection {
  const controller = new AbortController();
  let attempt = 0;
  let stopped = false;
  let timer: number | undefined;

  const run = async () => {
    while (!stopped) {
      try {
        // A stream is held open for minutes, so the token is refreshed before
        // each attempt rather than reused from whenever the page loaded.
        await ensureFreshSession(API_URL);
        const token = getAccessToken();
        if (!token) throw new Error("not signed in");

        const response = await fetch(
          `${API_URL}/events?groupId=${encodeURIComponent(groupId)}`,
          {
            credentials: "include",
            headers: { authorization: `Bearer ${token}`, "x-fc-client": "web" },
            signal: controller.signal,
          },
        );

        if (!response.ok || !response.body) {
          // 401 means the token aged out mid-stream; the next pass mints a new
          // one. 404 means we are not in this group and retrying is pointless.
          if (response.status === 404) return;
          throw new Error(`stream failed: ${String(response.status)}`);
        }

        attempt = 0;
        onStateChange?.(true);

        const reader = response.body
          .pipeThrough(new TextDecoderStream())
          .getReader();
        let buffer = "";

        while (!stopped) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += value;

          // SSE frames are separated by a blank line. A frame can arrive split
          // across reads, so anything after the last separator stays buffered.
          const frames = buffer.split("\n\n");
          buffer = frames.pop() ?? "";

          for (const frame of frames) {
            const line = frame
              .split("\n")
              .find((part) => part.startsWith("data:"));
            if (!line) continue; // A heartbeat comment, not an event.
            try {
              onEvent(JSON.parse(line.slice(5).trim()) as LiveEvent);
            } catch {
              // A malformed frame is not worth tearing the connection down for.
            }
          }
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        void error;
      }

      onStateChange?.(false);
      if (stopped) return;

      // The server closes idle streams and networks drop; reconnecting is the
      // normal case, not the exception.
      const wait = RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)]!;
      attempt += 1;
      await new Promise<void>((resolve) => {
        timer = window.setTimeout(resolve, wait);
      });
    }
  };

  void run();

  return {
    close() {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
      controller.abort();
    },
  };
}
